import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VStack, HStack } from '@astryxdesign/core/Stack';
import { Heading, Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { List, ListItem } from '@astryxdesign/core/List';
import { Banner } from '@astryxdesign/core/Banner';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { Icon } from '@astryxdesign/core/Icon';
import { CalendarBlank, Phone } from '@phosphor-icons/react';
import { useTenant } from '@/app/tenant-context';
import { ApiError } from '@/lib/api';
import { bookingStore } from '@/lib/booking-store';
import { dateTimeLabel, money, STATUS_LABEL, telHref } from '@/lib/format';
import { ReminderOffer } from './booking/ReminderOffer';
import { useShell } from './ClientShell';

/** A link from the studio (…/my#t=<token>) adds that booking to this device. */
function useClaimLinkToken() {
  const { slug, api } = useTenant();
  const navigate = useNavigate();
  const token = /(?:^|&)t=([A-Za-z0-9_-]{43,128})/.exec(window.location.hash.slice(1))?.[1];
  const q = useQuery({ queryKey: ['claim', slug, token], queryFn: () => api.booking(token!), enabled: !!token, retry: false });
  useEffect(() => {
    if (!token || !q.data) return;
    bookingStore.save(slug, { token, id: q.data.id, code: q.data.code, startsAt: q.data.starts_at, serviceName: q.data.service_name, savedAt: new Date().toISOString() });
    history.replaceState(null, '', window.location.pathname); // drop the token from the address bar
    navigate(`/my/${q.data.id}`, { replace: true });
  }, [token, q.data, slug, navigate]);
  return { claiming: !!token && q.isPending, failed: !!token && q.isError };
}

export function MyBookingPage() {
  const { slug } = useTenant();
  const { bookingId } = useParams();
  const claim = useClaimLinkToken();
  if (claim.claiming) return <Skeleton height={240} radius={3} />;
  const stored = bookingStore.list(slug);
  const current = bookingId ? stored.find((b) => b.id === bookingId) : stored.length === 1 ? stored[0] : undefined;

  return (
    <VStack gap={6} paddingInline={4} paddingBlockStart={8} className="safe-top">
      <Heading level={1}>Моя запись</Heading>
      {claim.failed && <Banner status="error" title="Ссылка на запись недействительна" description="Возможно, запись отменена. Позвоните в студию." />}
      {current ? <BookingDetails token={current.token} id={current.id} /> : <BookingList />}
    </VStack>
  );
}

function BookingList() {
  const { slug, tenant } = useTenant();
  const { openBooking } = useShell();
  const navigate = useNavigate();
  const stored = bookingStore.list(slug);
  if (stored.length === 0) {
    return (
      <EmptyState
        title="Записей на этом устройстве нет"
        description="Запись хранится в браузере, где её оформили. Если записывались с другого телефона — позвоните в студию."
        icon={<Icon icon={CalendarBlank} size="lg" />}
        actions={
          <VStack gap={2}>
            <Button label="Записаться" variant="primary" onClick={() => openBooking()} />
            <Button label="Позвонить" icon={<Icon icon={Phone} />} href={telHref(tenant.phone)} />
          </VStack>
        }
      />
    );
  }
  return (
    <List density="spacious" hasDividers>
      {stored.map((b) => (
        <ListItem
          key={b.id}
          label={b.serviceName}
          description={`${dateTimeLabel(b.startsAt, tenant.timezone)} · ${b.code}`}
          onClick={() => navigate(`/my/${b.id}`)}
        />
      ))}
    </List>
  );
}

function BookingDetails({ token, id }: { token: string; id: string }) {
  const { api, slug, tenant } = useTenant();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const q = useQuery({ queryKey: ['my-booking', slug, id], queryFn: () => api.booking(token) });
  const cancel = useMutation({
    mutationFn: () => api.cancel(token),
    onSuccess: (b) => {
      qc.setQueryData(['my-booking', slug, id], b);
      setConfirmCancel(false);
      void qc.invalidateQueries({ queryKey: ['availability'] });
    },
  });

  if (q.isPending) return <Skeleton height={240} radius={3} />;
  if (q.isError) {
    const gone = q.error instanceof ApiError && q.error.code === 'booking_not_found';
    return (
      <EmptyState
        title={gone ? 'Запись не найдена' : 'Не удалось загрузить запись'}
        description={gone ? 'Возможно, её удалили. Позвоните в студию, если это ошибка.' : (q.error as Error).message}
        actions={
          gone ? (
            <Button
              label="Убрать с устройства"
              onClick={() => {
                bookingStore.remove(slug, id);
                navigate('/my', { replace: true });
              }}
            />
          ) : (
            <Button label="Повторить" variant="primary" onClick={() => q.refetch()} />
          )
        }
      />
    );
  }

  const b = q.data;
  const cancelled = b.status === 'cancelled';
  return (
    <VStack gap={5}>
      <Card padding={4}>
        <VStack gap={4}>
          <VStack gap={1}>
            <Heading level={2}>{b.service_name}</Heading>
            <Text type="label" weight="semibold" color={cancelled ? 'secondary' : 'accent'}>
              {STATUS_LABEL[b.status]}
            </Text>
          </VStack>
          {b.is_demo && <Banner status="info" title="Демо-запись" description="Студия ещё не запущена." />}
          <MetadataList columns="single">
            <MetadataListItem label="Код">{b.code}</MetadataListItem>
            <MetadataListItem label="Когда">{dateTimeLabel(b.starts_at, b.timezone)}</MetadataListItem>
            <MetadataListItem label="Стоимость">{money(b.price_cents, b.currency, b.price_is_from)}</MetadataListItem>
            <MetadataListItem label="Автомобиль">{b.car}</MetadataListItem>
            <MetadataListItem label="Адрес">{b.tenant.address}</MetadataListItem>
          </MetadataList>
          <HStack gap={2} wrap="wrap">
            <Button label="Позвонить в студию" icon={<Icon icon={Phone} />} href={telHref(tenant.phone)} />
          </HStack>
        </VStack>
      </Card>

      {!cancelled && <ReminderOffer booking={b} token={token} />}

      {b.status === 'confirmed' &&
        (b.can_cancel ? (
          <Button label="Отменить запись" variant="destructive" width="100%" onClick={() => setConfirmCancel(true)} />
        ) : (
          <Text type="supporting">
            Онлайн-отмена была доступна до {dateTimeLabel(b.cancel_until, b.timezone)}. Чтобы изменить запись, позвоните в студию.
          </Text>
        ))}
      {cancel.isError && <Banner status="error" title="Не удалось отменить" description={(cancel.error as Error).message} />}

      <AlertDialog
        isOpen={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Отменить запись?"
        description={`${b.service_name}, ${dateTimeLabel(b.starts_at, b.timezone)}. Время освободится для других.`}
        actionLabel="Отменить запись"
        cancelLabel="Оставить"
        actionVariant="destructive"
        isActionLoading={cancel.isPending}
        onAction={() => cancel.mutate()}
      />
      {bookingStore.list(slug).length > 1 && <Button label="Все мои записи" variant="ghost" onClick={() => navigate('/my')} />}
    </VStack>
  );
}
